import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { tap, combineLatest, Subscription, Observable, map } from 'rxjs';
import { OverlayPanel } from 'primeng/overlaypanel';

import { ProductService } from '../../../../services/product.service';
import { ShiftValidationService } from '../../../../services/shift-validation/shift-validation.service';
import { ShiftContextService } from '../../../../services/shift-context.service';
import { GroupedLoadSummary, LineItem, LoadsSummary, LoadsSummaryItem } from '../../../../models/shift-validation/loads-summary';
import { ShiftContext } from '../../../../models/shift-context';

@Component({
    selector: 'app-loads-summary',
    templateUrl: './loads-summary.component.html',
    styleUrls: ['./loads-summary.component.scss']
})
export class LoadsSummaryComponent implements OnInit, OnDestroy {
    loadsSummary: LoadsSummary = { preloadedLoads: [], processedLoads: [], shippedLoads: [] };
    groupedProcessed: GroupedLoadSummary[];
    groupedPreLoaded: GroupedLoadSummary[];
    groupedShipped: GroupedLoadSummary[];
    lineItems: LineItem[] = [];
    loading = false;
    subscriptions: Subscription[];
    startupSubscription: Subscription;
    context: ShiftContext;

    submitted: boolean = false;

    @ViewChild('op', { static: false }) overlayPanel: OverlayPanel;

    constructor(
        private shiftContextService: ShiftContextService,
        private shiftValidationService: ShiftValidationService,
        private productService: ProductService) { }

    ngOnInit() {
        this.loading = true;

        this.subscriptions = [
            combineLatest([
                this.shiftContextService.CurrentContext.pipe(
                    tap(context => {
                        this.shiftValidationService.getLoadsSummary(context.id);
                        this.context = context;
                    })),
                    this.shiftValidationService.CurrentLoadsSummary
            ]).subscribe(([context, summary]) => {
                this.loading = !summary;

                if (summary) {
                    this.loadsSummary = summary;
                    this.groupAndSortLoads();
                }
            }),
        ];

    }
    groupAndSortLoads() {
        if (this.loadsSummary) {
            const groupedProcessed = this.groupByOperation(this.loadsSummary.processedLoads);
            const groupedPreloaded = this.groupByOperation(this.loadsSummary.preloadedLoads);
            const groupedShipped = this.groupByOperation(this.loadsSummary.shippedLoads);

            this.groupedPreLoaded = (Object.values(groupedPreloaded));
            this.groupedShipped = (Object.values(groupedShipped));
            this.groupedProcessed = (Object.values(groupedProcessed));
        }
    }

    groupByOperation(loads: LoadsSummaryItem[]): { [subinventoryId: number]: GroupedLoadSummary } {
        return loads.reduce((agg, curr) => {
            agg[curr.subinventoryId] = agg[curr.subinventoryId] ?? {
                id: curr.subinventoryId,
                subInventory: this.context.site.subinventories.find(s => s.id === curr.subinventoryId),
                items: [],
                piecesTotal: 0
            };
            agg[curr.subinventoryId].items.push(curr);
            agg[curr.subinventoryId].piecesTotal += curr.lineItems.reduce((tot, val) => tot + val.quantity, 0);
            return agg;
        }, <{ [subinventoryId: number]: GroupedLoadSummary }>{});
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.loadsSummary = { preloadedLoads: [], processedLoads: [], shippedLoads: [] };
        this.groupedPreLoaded = [];
        this.groupedShipped = [];
        this.groupedProcessed = [];
    }

    getProduct(productId: number): Observable<string> {
        return this.productService.FindProduct(productId).pipe(map(p => p?.label));
    }

    totalQuantity(lineItems: LineItem[]): number {
        return lineItems.map(x => x.quantity).reduce((val, tot) => val + tot, 0);
    }

    overlayToggle(event: any, lineItems: LineItem[]) {
        this.lineItems = lineItems;
        this.overlayPanel.show(event);
    }
}
